import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Switch,
  CardMedia,
  Tooltip,
  Collapse,
  IconButton,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Snackbar,
} from '@mui/material';
import {
  Launch as LaunchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useScrapingTasks, ScrapingTask } from '../hooks/useScrapingTasks';
import { useMarketplaceDefinition } from '../hooks/useMarketplaceDefinitions';
import { useThemeContext } from '../contexts/ThemeContext';
import { analyzeListingWithAI } from '../services/obpAiService';
import { enrichSellerDataFromTaskProducts, sellerDisplayName } from '../utils/listingSellers';
import VerifyAccessDialog from '../components/VerifyAccessDialog';
import { CaptchaTask, enrichCaptchaTaskFromResults } from '../utils/captchaAccess';

interface TaskResult {
  id: string;
  url: string;
  parse_success?: boolean;
  success?: boolean;
  response_time: number;
  html_content?: string;
  html_size?: number;
  extracted_data: any;
  listings?: any[];
  error_message?: string;
  crawled_at: string;
  status_code?: number;
  screenshot_url?: string;
  screenshot_timestamp?: number;
}

interface ProductListing {
  title?: string;
  price?: string;
  url?: string;
  image?: string;
  description?: string;
  seller?: string;
  condition?: string;
  location?: string;
  availability?: string;
  marketplace?: string;
  scraped_at?: string;
  scraping_stage?: string;
  is_duplicate?: boolean;
  duplicate_reason?: string;
  screenshot_url?: string;
  screenshot_captured_at?: string;
  [key: string]: any;
}

const isSearchStageListing = (p: any): boolean =>
  !!p && (p.stage === 'search' || p.scraping_stage === 'stage1_search_results');

const isProductStageListing = (p: any): boolean =>
  !!p && (p.stage === 'product' || p.scraping_stage === 'stage2_product_page');

function listingPrimaryImage(listing: any): string | undefined {
  const images = Array.isArray(listing?.images)
    ? listing.images.filter((u: unknown) => typeof u === 'string' && String(u).trim() !== '')
    : [];
  return listing?.image || images[0];
}

/** Crawl-result thumbnail: top-level screenshot, then listing screenshot, then product image. */
function resultScreenshotUrl(result: TaskResult): string | undefined {
  if (result.screenshot_url) {
    const listings = result.listings;
    if (
      !Array.isArray(listings) ||
      listings.length === 0 ||
      !listings.every(isProductStageListing)
    ) {
      return result.screenshot_url;
    }
    const img = listings.map(listingPrimaryImage).find(Boolean);
    if (img) return img;
    return result.screenshot_url;
  }
  if (!Array.isArray(result.listings)) return undefined;
  for (const listing of result.listings) {
    if (listing?.screenshot_url && !isProductStageListing(listing)) {
      return listing.screenshot_url;
    }
  }
  for (const listing of result.listings) {
    const img = listingPrimaryImage(listing);
    if (img) return img;
    if (listing?.screenshot_url) return listing.screenshot_url;
  }
  return undefined;
}

function listingPreviewUrl(listing: ProductListing): {
  url?: string;
  kind: 'screenshot' | 'product' | null;
} {
  const img = listingPrimaryImage(listing);
  // Product-stage page captures are often blank on SPAs (hidden body); prefer catalog image.
  if (isProductStageListing(listing) && img) {
    return { url: img, kind: 'product' };
  }
  if (listing.screenshot_url) return { url: listing.screenshot_url, kind: 'screenshot' };
  if (img) return { url: img, kind: 'product' };
  return { url: undefined, kind: null };
}

/** Format execution time in seconds as "X min Y s" or "X s". */
function formatExecutionTime(seconds: number): string {
  if (seconds >= 60) {
    const min = Math.floor(seconds / 60);
    const sec = Math.round(seconds % 60);
    return sec > 0 ? `${min} min ${sec} s` : `${min} min`;
  }
  return `${Math.round(seconds)} s`;
}

// Lightweight Lightbox dialog for displaying large screenshots
const LightboxDialog: React.FC<{
  open: boolean;
  src: string;
  onClose: () => void;
}> = ({ open, src, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl">
      <DialogContent sx={{ p: 0, bgcolor: 'black' }}>
        <img
          src={src}
          alt="Screenshot preview"
          style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block', margin: '0 auto' }}
          onError={(e: any) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.open(src, '_blank', 'noopener,noreferrer')}
        >
          Open in new tab
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ProductCard Component for individual product listings
// SellerCard Component for seller-focused results
const SellerCard: React.FC<{
  data: any;
  taskResults?: Array<{ listings?: any[]; extracted_data?: any }>;
  onOpenImage?: (url: string) => void;
}> = ({ data, taskResults = [], onOpenImage }) => {
  const enriched = enrichSellerDataFromTaskProducts(data, taskResults);
  const sellerName = sellerDisplayName(enriched);
  const sellerRating: string | undefined = enriched.seller_rating || enriched.rating;
  const location: string | undefined = enriched.location || enriched.address;
  const description: string | undefined = enriched.description;
  const screenshotUrl: string | undefined = enriched.screenshot_url || enriched.screenshotUrl;
  const sellerStoreUrlRaw: string | undefined =
    enriched.seller_store_url || enriched.store_url || enriched.url;

  // Amazon often returns relative store URLs that need the domain
  const sellerStoreUrl = (() => {
    if (!sellerStoreUrlRaw) return undefined;
    if (sellerStoreUrlRaw.startsWith('http')) return sellerStoreUrlRaw;
    // Default to Amazon US if domain is not provided
    return `https://www.amazon.com${sellerStoreUrlRaw}`;
  })();

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
              <Chip label="Seller" color="info" size="small" />
              {sellerRating && (
                <Chip label={sellerRating} color="secondary" size="small" variant="outlined" />
              )}
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              {sellerName}
            </Typography>

            {description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontStyle: 'italic' }}
              >
                {description}
              </Typography>
            )}

            {location && (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {location}
              </Typography>
            )}

            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              {sellerStoreUrl && (
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<LaunchIcon fontSize="small" />}
                  onClick={() => window.open(sellerStoreUrl, '_blank', 'noopener,noreferrer')}
                >
                  Open Seller Store
                </Button>
              )}
              {enriched.source_url && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => window.open(enriched.source_url, '_blank', 'noopener,noreferrer')}
                >
                  Open Source Page
                </Button>
              )}
            </Box>
          </Box>

          {screenshotUrl && (
            <Box
              sx={{
                width: 200,
                flexShrink: 0,
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: 2,
                cursor: 'zoom-in',
              }}
              onClick={() => onOpenImage && onOpenImage(screenshotUrl)}
            >
              <img
                src={screenshotUrl}
                alt="Seller screenshot"
                style={{ width: '100%', display: 'block', objectFit: 'cover' }}
                onError={(e: any) => (e.currentTarget.style.display = 'none')}
              />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
const ProductCard: React.FC<{
  listing: ProductListing;
  index: number;
  resultId: string;
  onOpenImage?: (url: string) => void;
}> = ({ listing, index, resultId, onOpenImage }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [assessment, setAssessment] = useState<string | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const handleProductClick = () => {
    if (listing.url) {
      window.open(listing.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleAnalyze = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnalyzing(true);
    setAnalysisError(null);
    try {
      const images: string[] = [];
      if (listing.image) images.push(listing.image);
      const res = await analyzeListingWithAI(
        {
          title: listing.title,
          price: listing.price,
          url: listing.url,
          image: listing.image,
          description: listing.description,
          seller: listing.seller,
          marketplace: listing.marketplace,
          images,
        },
        `${resultId}-${index}`
      );
      setAssessment(res.assessment);
    } catch (err: any) {
      setAnalysisError(err.message || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        cursor: listing.url ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        position: 'relative',
        '&:hover': listing.url
          ? {
              boxShadow: 6,
              transform: 'translateY(-3px)',
            }
          : {
              boxShadow: 2,
            },
      }}
      onClick={handleProductClick}
    >
      <Grid container>
        {/* Image Section */}
        {(() => {
          // Prefer explicit image; else fall back to first in images array
          const imagesArray = Array.isArray((listing as any).images)
            ? ((listing as any).images as string[]).filter(
                (u: any) => typeof u === 'string' && u.trim() !== ''
              )
            : [];
          const mainImage = listing.image || imagesArray[0];
          if (!mainImage) return null;
          return (
            <Grid item xs={12} sm={3}>
              <CardMedia
                component="img"
                sx={{
                  width: '100%',
                  height: 180,
                  objectFit: 'cover',
                  borderRadius: 1,
                }}
                image={mainImage}
                alt={listing.title || `Product ${index + 1}`}
                onError={(e: any) => {
                  e.target.style.display = 'none';
                  e.target.onerror = null;
                }}
              />
            </Grid>
          );
        })()}

        {/* Content Section */}
        <Grid
          item
          xs={12}
          sm={
            listing.image ||
            (Array.isArray((listing as any).images) && (listing as any).images.length > 0)
              ? 9
              : 12
          }
        >
          <CardContent>
            {/* Stage and Duplicate Indicators */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={1}
              gap={1}
            >
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip
                  label={isSearchStageListing(listing) ? 'Search Result' : 'Full Product'}
                  color={isSearchStageListing(listing) ? 'primary' : 'secondary'}
                  size="small"
                  variant="outlined"
                />
                {listing.is_duplicate && (
                  <Tooltip title={listing.duplicate_reason || 'This product was already scraped'}>
                    <Chip
                      label={
                        listing.duplicate_reason?.includes('store service')
                          ? `Store ${listing.duplicate_reason.includes('active') ? 'Active' : 'Archived'}`
                          : 'Local Duplicate'
                      }
                      color={
                        listing.duplicate_reason?.includes('store service')
                          ? listing.duplicate_reason.includes('active')
                            ? 'success'
                            : 'info'
                          : 'warning'
                      }
                      size="small"
                      variant="filled"
                      sx={{
                        opacity: 0.8,
                        '&:hover': { opacity: 1 },
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.2,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flex: 1,
                  mr: 1,
                  fontSize: '1rem',
                  minHeight: '3.6rem', // Ensure consistent height for 3 lines
                }}
              >
                {listing.title || 'Untitled Product'}
              </Typography>
              {listing.url && <LaunchIcon color="action" fontSize="small" />}
            </Box>

            {/* Image thumbnails (for product stage) */}
            {(() => {
              const imagesArray = Array.isArray((listing as any).images)
                ? ((listing as any).images as string[]).filter(
                    (u: any) => typeof u === 'string' && u.trim() !== ''
                  )
                : [];
              const stageValue = (listing as any).stage;
              const isProductStage = stageValue === 'product';
              if (!isProductStage || imagesArray.length <= 1) return null;
              // Skip the first one if it matches the main image
              const mainImage = listing.image || imagesArray[0];
              const thumbs = imagesArray.filter(u => u !== mainImage).slice(0, 6);
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {thumbs.map((u, i) => (
                    <CardMedia
                      key={`${u}-${i}`}
                      component="img"
                      image={u}
                      alt={`thumb-${i + 1}`}
                      sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1 }}
                      onError={(e: any) => (e.currentTarget.style.display = 'none')}
                    />
                  ))}
                </Box>
              );
            })()}

            {/* AI Assessment & Analyze Action */}
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {assessment && (
                <Chip
                  label={`AI: ${assessment}`}
                  color={
                    assessment === 'HIGH'
                      ? 'error'
                      : assessment === 'MEDIUM'
                        ? 'warning'
                        : assessment === 'LOW'
                          ? 'success'
                          : 'default'
                  }
                  size="small"
                />
              )}
              <Button variant="outlined" size="small" onClick={handleAnalyze} disabled={analyzing}>
                {analyzing ? (
                  <>
                    <CircularProgress size={14} sx={{ mr: 1 }} /> Analyzing
                  </>
                ) : (
                  'Analyze'
                )}
              </Button>
              {analysisError && (
                <Typography variant="caption" color="error">
                  {analysisError}
                </Typography>
              )}
            </Box>

            {listing.price && (
              <Typography variant="h6" color="primary" sx={{ mb: 1, fontWeight: 700 }}>
                {listing.price}
              </Typography>
            )}

            {listing.seller && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>Seller:</strong> {listing.seller}
              </Typography>
            )}

            {listing.condition && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>Condition:</strong> {listing.condition}
              </Typography>
            )}

            {listing.location && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>Location:</strong> {listing.location}
              </Typography>
            )}

            {listing.availability && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>Available:</strong> {listing.availability}
              </Typography>
            )}

            {listing.description && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: 1.4,
                  color: 'text.secondary',
                }}
              >
                {listing.description}
              </Typography>
            )}
          </CardContent>
        </Grid>
      </Grid>

      {/* Page capture or product image (bottom-right). SPAs may yield blank page
          screenshots when the DOM stays hidden; fall back to the product image. */}
      {(() => {
        const preview = listingPreviewUrl(listing);
        if (!preview.url) return null;
        return (
          <Box
            sx={{
              position: 'absolute',
              right: 8,
              bottom: 8,
              zIndex: 2,
              borderRadius: 1,
              overflow: 'hidden',
              boxShadow: 3,
            }}
            onClick={e => {
              e.stopPropagation();
              if (onOpenImage && preview.url) onOpenImage(preview.url);
            }}
          >
            <Tooltip
              title={
                (listing as any).stage2_requires_auth
                  ? `Challenge screenshot — ${(listing as any).stage2_auth_reason || 'auth required'}`
                  : preview.kind === 'product'
                    ? 'Product image (page capture unavailable)'
                    : 'Page screenshot'
              }
              arrow
            >
              <Box sx={{ position: 'relative' }}>
                <img
                  src={preview.url}
                  width={56}
                  height={56}
                  style={{ display: 'block', objectFit: 'cover' }}
                  onError={(e: any) => {
                    const fallback = listingPrimaryImage(listing);
                    if (fallback && e.currentTarget.src !== fallback) {
                      e.currentTarget.src = fallback;
                      return;
                    }
                    e.currentTarget.style.display = 'none';
                  }}
                  alt={preview.kind === 'product' ? 'Product image' : 'Screenshot thumbnail'}
                />
                {(listing as any).stage2_requires_auth && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: 'warning.main',
                      color: 'warning.contrastText',
                      fontSize: '0.6em',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      py: 0.25,
                      letterSpacing: '0.05em',
                    }}
                  >
                    AUTH
                  </Box>
                )}
              </Box>
            </Tooltip>
          </Box>
        );
      })()}
    </Card>
  );
};

const TaskResults: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const [taskResults, setTaskResults] = useState<TaskResult[]>([]);
  const [task, setTask] = useState<ScrapingTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [hideDuplicates, setHideDuplicates] = useState<boolean>(true);
  const { getTaskResults, getTask } = useScrapingTasks();
  const { isDarkMode } = useThemeContext();

  // Fetch marketplace definition details
  const { data: marketplaceDefinition } = useMarketplaceDefinition(
    task?.marketplace_definition_id || ''
  );

  // Lightbox viewer state
  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  const [lightboxSrc, setLightboxSrc] = useState<string>('');
  const [captchaDialogOpen, setCaptchaDialogOpen] = useState(false);
  const [captchaTask, setCaptchaTask] = useState<CaptchaTask | null>(null);
  const [captchaStatusMessage, setCaptchaStatusMessage] = useState<string | null>(null);

  const openVerifyAccessDialog = () => {
    if (!task) return;
    setCaptchaTask(enrichCaptchaTaskFromResults(task, taskResults));
    setCaptchaDialogOpen(true);
  };

  const openLightbox = (src: string) => {
    setLightboxSrc(src);
    setLightboxOpen(true);
  };
  const closeLightbox = () => setLightboxOpen(false);

  // Helper function to toggle row expansion
  const toggleRowExpansion = (resultId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(resultId)) {
        newSet.delete(resultId);
      } else {
        newSet.add(resultId);
      }
      return newSet;
    });
  };

  // Helper function to check if result contains product listings
  const isSearchResult = (result: TaskResult): boolean => {
    console.log('isSearchResult called with:', result);

    // Check if it has listings array
    if (result.listings && Array.isArray(result.listings) && result.listings.length > 0) {
      console.log('isSearchResult: found listings array with', result.listings.length, 'items');
      return true;
    }

    // Check if extracted_data is an array of products
    if (Array.isArray(result.extracted_data) && result.extracted_data.length > 0) {
      const firstItem = result.extracted_data[0];
      const hasProductFields = typeof firstItem === 'object' && (firstItem.title || firstItem.url);
      console.log('isSearchResult: extracted_data is array, hasProductFields:', hasProductFields);
      return hasProductFields;
    }

    // Check if it's a search result object with items array
    if (result.extracted_data && typeof result.extracted_data === 'object') {
      const data = result.extracted_data;
      if (data.items && Array.isArray(data.items) && data.items.length > 0) {
        const firstItem = data.items[0];
        const hasProductFields =
          typeof firstItem === 'object' && (firstItem.title || firstItem.url);
        console.log(
          'isSearchResult: extracted_data.items is array, hasProductFields:',
          hasProductFields
        );
        return hasProductFields;
      }
      // Check for eBay-style structure
      if (data.total_items && data.total_listings && typeof data.total_items === 'number') {
        console.log(
          'isSearchResult: found eBay-style structure with total_items:',
          data.total_items
        );
        return true;
      }
    }

    console.log('isSearchResult: no search result structure found');
    return false;
  };

  // Detect if a result represents a seller page extraction
  const isSellerResult = (result: TaskResult): boolean => {
    const d = result.extracted_data as any;
    return !!(
      d &&
      typeof d === 'object' &&
      (d.page_type === 'seller' ||
        ((d.seller || d.seller_name) &&
          (d.location || d.address || d.seller_rating || d.seller_store_url)))
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!taskId) return;

      try {
        setLoading(true);

        // Fetch both task details and results in parallel
        const [taskData, results] = await Promise.all([getTask(taskId), getTaskResults(taskId)]);

        // Set task details
        setTask(taskData);

        // Set task results
        if (results && results.items) {
          setTaskResults(results.items);
        } else {
          setTaskResults([]);
        }

        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch task data');
        setTaskResults([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [taskId, getTaskResults, getTask]);

  // Robust success/failure detection for a result
  const isSuccessfulResult = (r: TaskResult): boolean => {
    // First check the explicit success field from backend
    if (r.success === true || r.parse_success === true) return true;
    if (r.success === false || r.parse_success === false) return false;

    // Fallback: check if we have any data extracted
    // 1) listings array present
    if (Array.isArray(r.listings) && r.listings.length > 0) return true;
    // 2) extracted_data totals style
    if (r.extracted_data && typeof r.extracted_data === 'object') {
      if (
        typeof r.extracted_data.total_listings === 'number' &&
        r.extracted_data.total_listings > 0
      )
        return true;
      // 3) array in extracted_data
      if (Array.isArray(r.extracted_data) && r.extracted_data.length > 0) return true;
      // 4) items array inside extracted_data
      if (Array.isArray(r.extracted_data.items) && r.extracted_data.items.length > 0) return true;
      // 5) seller/product data with extracted fields
      if (
        r.extracted_data.page_type === 'seller' ||
        r.extracted_data.page_type === 'product_listing'
      ) {
        const extractedFields = r.extracted_data.extracted_fields || 0;
        if (extractedFields > 0) return true;
      }
    }
    return false;
  };

  const successfulResults = taskResults.filter(r => isSuccessfulResult(r));
  const failedResults = taskResults.filter(r => !isSuccessfulResult(r));

  // Normalize URL for comparison (strip query params so requested URL matches result URL)
  const normalizeUrlForComparison = (url: string): string => {
    try {
      const u = url.trim().toLowerCase();
      const q = u.indexOf('?');
      return q >= 0 ? u.slice(0, q) : u;
    } catch {
      return url;
    }
  };
  const requestedUrlSet =
    task?.urls && task.urls.length > 0
      ? new Set((task.urls as string[]).map(normalizeUrlForComparison))
      : null;
  // Only count failures for URLs that were explicitly requested (not e.g. seller profile follow-up)
  const failedRequestedResults =
    requestedUrlSet != null
      ? failedResults.filter(r => requestedUrlSet.has(normalizeUrlForComparison(r.url)))
      : failedResults;

  // Sort results for table: requested URLs first (in task order), then auxiliary (e.g. seller) last.
  // So "the last row" is clearly the seller/auxiliary page that often hits CAPTCHA, not the product.
  const sortedTaskResultsForTable =
    requestedUrlSet != null && (task?.urls as string[])?.length
      ? (() => {
          const order = task!.urls as string[];
          const requested: TaskResult[] = [];
          const seen = new Set<string>();
          for (const url of order) {
            const n = normalizeUrlForComparison(url);
            const matches = taskResults.filter(x => normalizeUrlForComparison(x.url) === n);
            const r = matches.sort((a, b) => {
              const aCheckpoint =
                (a.extracted_data as any)?.stage1_checkpoint ||
                (a.extracted_data as any)?.product_checkpoint;
              const bCheckpoint =
                (b.extracted_data as any)?.stage1_checkpoint ||
                (b.extracted_data as any)?.product_checkpoint;
              if (!!aCheckpoint !== !!bCheckpoint) return aCheckpoint ? 1 : -1;
              return (b.listings?.length ?? 0) - (a.listings?.length ?? 0);
            })[0];
            if (r && !seen.has(r.id)) {
              seen.add(r.id);
              requested.push(r);
            }
          }
          const other = taskResults
            .filter(r => !requestedUrlSet!.has(normalizeUrlForComparison(r.url)))
            .sort((a, b) => {
              const ta = a.crawled_at ? new Date(a.crawled_at).getTime() : 0;
              const tb = b.crawled_at ? new Date(b.crawled_at).getTime() : 0;
              return ta - tb;
            });
          return [...requested, ...other];
        })()
      : taskResults;

  // Categorize results by parsing success
  const parsingFailedResults = taskResults.filter(
    r =>
      r.extracted_data &&
      r.extracted_data.skipped_items > 0 &&
      r.extracted_data.total_listings === 0 &&
      r.extracted_data.total_items > 0
  );
  const partialSuccessResults = taskResults.filter(
    r =>
      r.extracted_data && r.extracted_data.skipped_items > 0 && r.extracted_data.total_listings > 0
  );

  // Use task-level metrics when task is completed to align with task list (avoids mismatch
  // when Stage 2 times out but Stage 1 checkpoint is stored - crawl_results show 1 success,
  // while task.success_rate correctly reflects 0/1 URLs completed).
  const useTaskMetrics =
    task &&
    (task.status === 'completed' || task.status === 'failed') &&
    typeof task.success_rate === 'number' &&
    typeof task.total_urls === 'number';

  const totalUrlsForDisplay = useTaskMetrics ? task!.total_urls : taskResults.length;
  const successRate = useTaskMetrics
    ? (task!.success_rate ?? 0) * 100
    : taskResults.length > 0
      ? (successfulResults.length / taskResults.length) * 100
      : 0;
  const successfulCount = useTaskMetrics
    ? Math.round((task!.processed_urls ?? task!.total_urls) * (task!.success_rate ?? 0))
    : successfulResults.length;
  const failedCount = useTaskMetrics
    ? (task!.processed_urls ?? task!.total_urls) - successfulCount
    : failedRequestedResults.length;

  const averageResponseTime =
    successfulResults.length > 0
      ? successfulResults.reduce((sum, r) => sum + (r.response_time || 0), 0) /
        successfulResults.length
      : 0;

  // Calculate duplicate statistics from all listings
  const allListings = taskResults.flatMap(r => r.listings || []);
  const duplicateListings = allListings.filter(l => l.is_duplicate);
  const duplicateCount = duplicateListings.length;

  // Determine which stages are present in the results
  const getResultStage = (result: TaskResult): string | null => {
    if (isSellerResult(result)) {
      return 'seller';
    }

    let hasStage1 = false;
    let hasStage2 = false;

    if (result.listings && Array.isArray(result.listings)) {
      hasStage1 = result.listings.some((product: any) => product.stage === 'search');
      hasStage2 = result.listings.some((product: any) => product.stage === 'product');
    } else if (result.extracted_data) {
      if (Array.isArray(result.extracted_data)) {
        hasStage1 = result.extracted_data.some((product: any) => product.stage === 'search');
        hasStage2 = result.extracted_data.some((product: any) => product.stage === 'product');
      } else if (result.extracted_data.items && Array.isArray(result.extracted_data.items)) {
        hasStage1 = result.extracted_data.items.some((product: any) => product.stage === 'search');
        hasStage2 = result.extracted_data.items.some((product: any) => product.stage === 'product');
      }
    }

    if (hasStage1 && hasStage2) {
      return 'mixed';
    } else if (hasStage1) {
      return 'search';
    } else if (hasStage2) {
      return 'product';
    }

    return null;
  };

  const stagesPresent = new Set<string>();
  taskResults.forEach(result => {
    const stage = getResultStage(result);
    if (stage) {
      stagesPresent.add(stage);
    }
  });

  const hasSellerResults = stagesPresent.has('seller');
  const hasSearchResults = stagesPresent.has('search');
  const hasProductResults = stagesPresent.has('product');
  const hasMixedResults = stagesPresent.has('mixed');

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <Typography>Loading task results...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  if (taskResults.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <Typography>No results found for this task.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Enhanced Header with Task Information */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}
        >
          <Box>
            <Typography variant="h4" gutterBottom>
              Task Results
            </Typography>
            {marketplaceDefinition && (
              <Typography variant="subtitle1" color="text.secondary" sx={{ mt: -1 }}>
                Marketplace: {marketplaceDefinition.name}
              </Typography>
            )}
          </Box>

          {/* View Marketplace Definition Button */}
          {task?.marketplace_definition_id && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<LaunchIcon />}
                onClick={() => {
                  // If definition has a parent, navigate to parent for reviewing inherited config
                  const targetId =
                    marketplaceDefinition?.parent_definition_id || task.marketplace_definition_id;
                  navigate(`/definitions/${targetId}/edit`);
                }}
              >
                {marketplaceDefinition?.parent_definition_id
                  ? 'View Parent Definition'
                  : 'View Definition'}
              </Button>
              {marketplaceDefinition?.parent_definition_id && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LaunchIcon />}
                  onClick={() => {
                    navigate(`/definitions/${task.marketplace_definition_id}/edit`);
                  }}
                >
                  View Local Definition
                </Button>
              )}
            </Box>
          )}
        </Box>

        {task && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 1 }}>
            <Chip
              label={task.status}
              color={
                task.status === 'completed'
                  ? 'success'
                  : task.status === 'failed'
                    ? 'error'
                    : 'warning'
              }
              variant="outlined"
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              Created: {new Date(task.created_at).toLocaleDateString()}
            </Typography>
            {task.estimated_completion && (
              <Typography variant="body2" color="text.secondary">
                Estimated: {new Date(task.estimated_completion).toLocaleDateString()}
              </Typography>
            )}
            {task.execution_time_seconds != null && (
              <Typography variant="body2" color="text.secondary">
                Total time: {formatExecutionTime(task.execution_time_seconds)}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              ID: {taskId}
            </Typography>

            {task.status === 'running' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Progress: {task.progress.toFixed(1)}%
                </Typography>
                <Box
                  sx={{
                    width: 100,
                    bgcolor: isDarkMode ? 'grey.700' : 'grey.200',
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${task.progress}%`,
                      height: 8,
                      bgcolor: 'primary.main',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Compact Summary Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Total URLs
              </Typography>
              <Typography variant="h5">{totalUrlsForDisplay}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Successful
              </Typography>
              <Typography variant="h5" color="success.main">
                {successfulCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Failed
              </Typography>
              <Typography variant="h5" color="error.main">
                {failedCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Success Rate
              </Typography>
              <Typography variant="h5">{Number(successRate).toFixed(1)}%</Typography>
            </CardContent>
          </Card>
        </Grid>
        {task?.execution_time_seconds != null && (
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  Total time
                </Typography>
                <Typography variant="h5">
                  {formatExecutionTime(task.execution_time_seconds)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid item xs={6} sm={4}>
          <Card>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Avg Response Time
              </Typography>
              <Typography variant="h6">{averageResponseTime.toFixed(2)}s</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Card>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Status
              </Typography>
              <Tooltip
                title={
                  failedCount > 0 ? (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}
                      >
                        Failed URLs:
                      </Typography>
                      {failedRequestedResults.map((r, idx) => (
                        <Box key={r.id} sx={{ mb: 0.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', fontFamily: 'monospace' }}
                          >
                            {idx + 1}. {r.url.length > 60 ? r.url.substring(0, 60) + '...' : r.url}
                          </Typography>
                          {r.error_message && (
                            <Typography
                              variant="caption"
                              sx={{ display: 'block', ml: 2, color: 'error.light' }}
                            >
                              → {r.error_message}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    'All URLs scraped successfully'
                  )
                }
                arrow
                placement="top"
              >
                <Chip
                  label={failedCount > 0 ? `${failedCount} failed` : 'All successful'}
                  color={failedCount > 0 ? 'warning' : 'success'}
                  size="small"
                  sx={{ cursor: 'help' }}
                />
              </Tooltip>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Duplicates
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" color="warning.main">
                  {duplicateCount}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hideDuplicates}
                      onChange={e => setHideDuplicates(e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="caption">Hide</Typography>}
                  sx={{ m: 0 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Blocking Detection Alert - only for requested URLs that hit CAPTCHA/auth */}
      {(() => {
        const blockedResults = taskResults.filter(
          result =>
            result.error_message &&
            (result.error_message.toLowerCase().includes('blocking page detected') ||
              result.error_message.toLowerCase().includes('captcha') ||
              result.error_message.toLowerCase().includes('login required') ||
              result.error_message.toLowerCase().includes('authentication or captcha required'))
        );
        const blockedRequestedResults =
          requestedUrlSet != null
            ? blockedResults.filter(r => requestedUrlSet.has(normalizeUrlForComparison(r.url)))
            : blockedResults;

        if (blockedRequestedResults.length > 0) {
          const captchaResults = blockedRequestedResults.filter(r =>
            r.error_message?.toLowerCase().includes('captcha')
          );
          const loginResults = blockedRequestedResults.filter(r =>
            r.error_message?.toLowerCase().includes('login required')
          );
          // Surface the captured challenge screenshots inline — the
          // table row's 48px thumbnail is too small to read what the
          // marketplace actually returned (auth gate vs CAPTCHA vs
          // verification puzzle). Showing them here lets the user
          // diagnose at a glance.
          const blockedWithScreenshots = blockedRequestedResults.filter(r => r.screenshot_url);

          return (
            <Card
              sx={{
                mb: 3,
                bgcolor: 'warning.light',
                border: '1px solid',
                borderColor: 'warning.main',
              }}
            >
              <CardContent>
                <Typography variant="h6" color="warning.dark" gutterBottom>
                  🔐 Authentication Required
                </Typography>
                <Typography variant="body2" color="warning.dark">
                  {captchaResults.length > 0 &&
                    `${captchaResults.length} URL(s) encountered CAPTCHA challenges. `}
                  {loginResults.length > 0 &&
                    `${loginResults.length} URL(s) require login authentication. `}
                  Use the Authentix Chrome extension to export authentication data and retry the
                  scraping task.
                </Typography>

                {blockedWithScreenshots.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="caption"
                      color="warning.dark"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      Captured at the time of failure (click to enlarge):
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                      }}
                    >
                      {blockedWithScreenshots.map((r, idx) => (
                        <Tooltip key={`${r.url}-${idx}`} title={r.url} arrow placement="top">
                          <Box
                            component="img"
                            src={r.screenshot_url}
                            alt={`Challenge screenshot for ${r.url}`}
                            onClick={() => openLightbox(r.screenshot_url as string)}
                            onError={(e: any) => {
                              e.currentTarget.style.display = 'none';
                            }}
                            sx={{
                              width: 180,
                              height: 120,
                              objectFit: 'cover',
                              objectPosition: 'top',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'warning.main',
                              cursor: 'zoom-in',
                              boxShadow: 1,
                              transition: 'transform 120ms ease-out',
                              '&:hover': {
                                transform: 'scale(1.02)',
                              },
                            }}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                )}

                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={openVerifyAccessDialog}
                  >
                    Open Extension Guide
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        }
        // Softer message when only non-requested URLs (e.g. seller profile) hit CAPTCHA
        if (blockedResults.length > 0 && requestedUrlSet != null) {
          return (
            <Card
              sx={{ mb: 3, bgcolor: 'info.light', border: '1px solid', borderColor: 'info.main' }}
            >
              <CardContent>
                <Typography variant="body2" color="info.dark">
                  Seller or auxiliary page(s) encountered CAPTCHA; listing and seller data from the
                  product page(s) were saved successfully. Use the extension if you need full seller
                  profile data.
                </Typography>
              </CardContent>
            </Card>
          );
        }
        return null;
      })()}

      {/* Parsing Failure Alert */}
      {(() => {
        if (parsingFailedResults.length > 0 || partialSuccessResults.length > 0) {
          const totalSkippedItems = taskResults.reduce(
            (sum, r) => sum + (r.extracted_data?.skipped_items || 0),
            0
          );
          const totalItems = taskResults.reduce(
            (sum, r) => sum + (r.extracted_data?.total_items || 0),
            0
          );

          return (
            <Card
              sx={{
                mb: 3,
                border: '1px solid',
                borderColor: 'warning.main',
              }}
            >
              <CardContent>
                <Typography variant="h6" color="warning.dark" gutterBottom>
                  ⚠️ Parsing Issues Detected
                </Typography>
                <Typography variant="body2" color="warning.dark">
                  {parsingFailedResults.length > 0 &&
                    `${parsingFailedResults.length} URL(s) failed to extract any data due to missing required fields. `}
                  {partialSuccessResults.length > 0 &&
                    `${partialSuccessResults.length} URL(s) had partial success with some items skipped. `}
                  Total: {totalSkippedItems} items skipped out of {totalItems} items found. This may
                  indicate that the marketplace definition needs updating or the page structure has
                  changed.
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={() => {
                      // Navigate to the specific marketplace definition used by this task
                      const definitionId = task?.marketplace_definition_id;
                      if (definitionId) {
                        window.open(`/definitions/${definitionId}/edit`, '_blank');
                      } else {
                        window.open('/definitions', '_blank');
                      }
                    }}
                  >
                    Review Marketplace Definition
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        }
        return null;
      })()}

      {/* Results Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            URL Results
          </Typography>

          {/* Stage Legend */}
          <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {hasSellerResults && (
              <>
                <Chip label="Seller" color="info" size="small" variant="outlined" />
                <Typography variant="body2" color="text.secondary">
                  Seller information pages
                </Typography>
              </>
            )}
            {hasSearchResults && (
              <>
                <Chip label="Search Results" color="primary" size="small" variant="outlined" />
                <Typography variant="body2" color="text.secondary">
                  Multiple products from search pages
                </Typography>
              </>
            )}
            {hasProductResults && (
              <>
                <Chip label="Full Products" color="secondary" size="small" variant="outlined" />
                <Typography variant="body2" color="text.secondary">
                  Individual product details
                </Typography>
              </>
            )}
            {hasMixedResults && (
              <>
                <Chip label="Mixed Stages" color="warning" size="small" variant="outlined" />
                <Typography variant="body2" color="text.secondary">
                  Both search and product results
                </Typography>
              </>
            )}
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>URL</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Response Time</TableCell>
                  <TableCell>HTTP Code</TableCell>
                  <TableCell>Screenshot</TableCell>
                  <TableCell>Stage</TableCell>
                  <TableCell>Data Extracted</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedTaskResultsForTable.map(result => (
                  <React.Fragment key={result.id}>
                    <TableRow key={result.id}>
                      <TableCell>
                        <Tooltip title={`Open ${result.url} in new tab`} arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 300,
                              wordBreak: 'break-all',
                              cursor: 'pointer',
                              color: 'primary.main',
                              textDecoration: 'underline',
                              '&:hover': { opacity: 0.8 },
                            }}
                            onClick={() => window.open(result.url, '_blank', 'noopener,noreferrer')}
                          >
                            {result.url}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Check if this is a blocking scenario
                          const isBlocking =
                            result.error_message &&
                            (result.error_message
                              .toLowerCase()
                              .includes('blocking page detected') ||
                              result.error_message.toLowerCase().includes('captcha') ||
                              result.error_message.toLowerCase().includes('login required') ||
                              result.error_message
                                .toLowerCase()
                                .includes('authentication or captcha required'));

                          if (isBlocking) {
                            const blockedUrl = (result.extracted_data as any)?.blocked_url;
                            const requiresAuth = (result as any)?.requires_auth;
                            const isCaptcha =
                              (result.error_message?.toLowerCase()?.includes('captcha') ||
                                requiresAuth) &&
                              !!blockedUrl;
                            if (isCaptcha) {
                              return 'warning';
                            }
                            return 'warning';
                          }

                          // Check for parsing failures (items skipped due to missing required fields)
                          const hasParsingFailures =
                            result.extracted_data && result.extracted_data.skipped_items > 0;
                          const hasNoSuccessfulExtractions =
                            result.extracted_data &&
                            result.extracted_data.total_listings === 0 &&
                            result.extracted_data.total_items > 0;

                          if (hasParsingFailures && hasNoSuccessfulExtractions) {
                            return <Chip label="Parsing Failed" color="error" size="small" />;
                          } else if (hasParsingFailures) {
                            return <Chip label="Partial Success" color="warning" size="small" />;
                          }

                          return (
                            <Chip
                              label={result.parse_success || result.success ? 'Success' : 'Failed'}
                              color={result.parse_success || result.success ? 'success' : 'error'}
                              size="small"
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {result.response_time ? `${result.response_time.toFixed(2)}s` : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={result.status_code || 'N/A'}
                          color={result.status_code === 200 ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {/* Result-level screenshot thumbnail (clickable) */}
                        {(() => {
                          const thumbUrl = resultScreenshotUrl(result);
                          return thumbUrl ? (
                            <Tooltip title="Preview screenshot" arrow>
                              <img
                                src={thumbUrl}
                                width={48}
                                height={48}
                                style={{
                                  display: 'block',
                                  objectFit: 'cover',
                                  borderRadius: 4,
                                  cursor: 'zoom-in',
                                }}
                                onClick={() => openLightbox(thumbUrl)}
                                onError={(e: any) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                                alt="Result screenshot thumbnail"
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Check if we have any products with stage information
                          let hasStage1 = false;
                          let hasStage2 = false;

                          // Seller result takes precedence as its own stage
                          if (isSellerResult(result)) {
                            return (
                              <Chip label="Seller" color="info" size="small" variant="outlined" />
                            );
                          }

                          if (result.listings && Array.isArray(result.listings)) {
                            hasStage1 = result.listings.some(
                              (product: any) => product.stage === 'search'
                            );
                            hasStage2 = result.listings.some(
                              (product: any) => product.stage === 'product'
                            );
                          } else if (result.extracted_data) {
                            if (Array.isArray(result.extracted_data)) {
                              hasStage1 = result.extracted_data.some(
                                (product: any) => product.stage === 'search'
                              );
                              hasStage2 = result.extracted_data.some(
                                (product: any) => product.stage === 'product'
                              );
                            } else if (
                              result.extracted_data.items &&
                              Array.isArray(result.extracted_data.items)
                            ) {
                              hasStage1 = result.extracted_data.items.some(
                                (product: any) => product.stage === 'search'
                              );
                              hasStage2 = result.extracted_data.items.some(
                                (product: any) => product.stage === 'product'
                              );
                            }
                          }

                          if (hasStage1 && hasStage2) {
                            return (
                              <Chip
                                label="Mixed Stages"
                                color="warning"
                                size="small"
                                variant="outlined"
                              />
                            );
                          } else if (hasStage1) {
                            return (
                              <Chip
                                label="Search Results"
                                color="primary"
                                size="small"
                                variant="outlined"
                              />
                            );
                          } else if (hasStage2) {
                            return (
                              <Chip
                                label="Full Products"
                                color="secondary"
                                size="small"
                                variant="outlined"
                              />
                            );
                          } else {
                            return (
                              <Chip
                                label="No Data"
                                color="default"
                                size="small"
                                variant="outlined"
                              />
                            );
                          }
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Show listings count if available
                          if (
                            result.listings &&
                            Array.isArray(result.listings) &&
                            result.listings.length > 0
                          ) {
                            const stage1Products = result.listings.filter(isSearchStageListing);
                            const stage2Products = result.listings.filter(isProductStageListing);
                            const hasMixed = stage1Products.length > 0 && stage2Products.length > 0;
                            const isProductRow =
                              stage2Products.length > 0 && stage1Products.length === 0;
                            const displayCount = isProductRow
                              ? stage2Products.length
                              : stage1Products.length > 0
                                ? stage1Products.length
                                : result.listings.length;
                            const fullCount = stage2Products.length;
                            const hasFullProducts = hasMixed && fullCount > 0;
                            const chipLabel = isProductRow
                              ? 'Full Product'
                              : hasMixed
                                ? 'Mixed Stages'
                                : 'Search Results';
                            const chipColor = isProductRow
                              ? 'secondary'
                              : hasMixed
                                ? 'warning'
                                : 'primary';

                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={chipLabel}
                                  color={chipColor}
                                  size="small"
                                  variant="outlined"
                                />
                                <Typography
                                  variant="body2"
                                  color="success.main"
                                  sx={{
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' },
                                  }}
                                  onClick={() => toggleRowExpansion(result.id)}
                                >
                                  <strong>{displayCount} products found</strong>
                                  {hasFullProducts && (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ ml: 1 }}
                                    >
                                      ({fullCount} have full details — see rows below)
                                    </Typography>
                                  )}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => toggleRowExpansion(result.id)}
                                  sx={{ p: 0.5 }}
                                >
                                  {expandedRows.has(result.id) ? (
                                    <ExpandLessIcon fontSize="small" />
                                  ) : (
                                    <ExpandMoreIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Box>
                            );
                          }

                          // Fall back to extracted_data display
                          if (
                            result.extracted_data &&
                            (Array.isArray(result.extracted_data) ||
                              Object.keys(result.extracted_data).length > 0)
                          ) {
                            return (
                              <Stack spacing={0.5}>
                                {isSellerResult(result) ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                      variant="body2"
                                      color="success.main"
                                      sx={{
                                        cursor: 'pointer',
                                        '&:hover': { textDecoration: 'underline' },
                                      }}
                                      onClick={() => toggleRowExpansion(result.id)}
                                    >
                                      <strong>View seller details</strong>
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => toggleRowExpansion(result.id)}
                                      sx={{ p: 0.5 }}
                                    >
                                      {expandedRows.has(result.id) ? (
                                        <ExpandLessIcon fontSize="small" />
                                      ) : (
                                        <ExpandMoreIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Box>
                                ) : isSearchResult(result) ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                      label="Search Results"
                                      color="primary"
                                      size="small"
                                      variant="outlined"
                                    />
                                    {(() => {
                                      // Check if we have both stages
                                      const stage1Products = result.extracted_data.filter(
                                        (product: any) => product.stage === 'search'
                                      );
                                      const stage2Products = result.extracted_data.filter(
                                        (product: any) => product.stage === 'product'
                                      );

                                      const filteredCount =
                                        stage1Products.length > 0 && stage2Products.length > 0
                                          ? stage2Products.length +
                                            stage1Products.filter(
                                              (product: any) =>
                                                !product.url ||
                                                !stage2Products.some(
                                                  (p: any) => p.url === product.url
                                                )
                                            ).length
                                          : result.extracted_data.length;

                                      const hasFullProducts =
                                        stage1Products.length > 0 && stage2Products.length > 0;

                                      return (
                                        <>
                                          <Typography
                                            variant="body2"
                                            color="success.main"
                                            sx={{
                                              cursor: 'pointer',
                                              '&:hover': { textDecoration: 'underline' },
                                            }}
                                            onClick={() => toggleRowExpansion(result.id)}
                                          >
                                            <strong>{filteredCount} products found</strong>
                                            {hasFullProducts && (
                                              <Typography
                                                component="span"
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ ml: 1 }}
                                              >
                                                ({result.extracted_data.length - filteredCount} have
                                                full details)
                                              </Typography>
                                            )}
                                          </Typography>
                                          <IconButton
                                            size="small"
                                            onClick={() => toggleRowExpansion(result.id)}
                                            sx={{ p: 0.5 }}
                                          >
                                            {expandedRows.has(result.id) ? (
                                              <ExpandLessIcon fontSize="small" />
                                            ) : (
                                              <ExpandMoreIcon fontSize="small" />
                                            )}
                                          </IconButton>
                                        </>
                                      );
                                    })()}
                                  </Box>
                                ) : Array.isArray(result.extracted_data) ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                      label="Full Product"
                                      color="secondary"
                                      size="small"
                                      variant="outlined"
                                    />
                                    <Typography variant="body2">
                                      Array with {result.extracted_data.length} items
                                    </Typography>
                                  </Box>
                                ) : (
                                  // Summary object with no listing rows — do not imply a product extract
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {(result.extracted_data?.total_listings ||
                                      result.extracted_data?.stage1_listings ||
                                      result.extracted_data?.total_items) > 0 ? (
                                      <Chip
                                        label="Extracted"
                                        color="secondary"
                                        size="small"
                                        variant="outlined"
                                      />
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        No products
                                      </Typography>
                                    )}
                                    <Typography
                                      variant="body2"
                                      color="primary"
                                      sx={{
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        '&:hover': { opacity: 0.8 },
                                      }}
                                      onClick={() => toggleRowExpansion(result.id)}
                                    >
                                      View details
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => toggleRowExpansion(result.id)}
                                      sx={{ p: 0.5 }}
                                    >
                                      {expandedRows.has(result.id) ? (
                                        <ExpandLessIcon fontSize="small" />
                                      ) : (
                                        <ExpandMoreIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Box>
                                )}
                              </Stack>
                            );
                          }

                          return (
                            <Typography variant="body2">
                              {result.html_size && result.html_size > 0
                                ? `HTML captured (${result.html_size} bytes)`
                                : 'No data'}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const hasErrorMessage = !!result.error_message;
                          const skippedItems = result.extracted_data?.skipped_items_details || [];
                          const hasSkippedItems = skippedItems.length > 0;

                          if (!hasErrorMessage && !hasSkippedItems)
                            return <Typography variant="body2">-</Typography>;

                          // Check if this is a blocking scenario
                          const isBlocking =
                            hasErrorMessage &&
                            result.error_message &&
                            (result.error_message
                              .toLowerCase()
                              .includes('blocking page detected') ||
                              result.error_message.toLowerCase().includes('captcha') ||
                              result.error_message.toLowerCase().includes('login required'));

                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {isBlocking && result.error_message && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip
                                    label={
                                      result.error_message.toLowerCase().includes('captcha')
                                        ? 'CAPTCHA'
                                        : result.error_message
                                              .toLowerCase()
                                              .includes('login required')
                                          ? 'Login Required'
                                          : 'Blocked'
                                    }
                                    color={
                                      result.error_message.toLowerCase().includes('captcha')
                                        ? 'warning'
                                        : 'error'
                                    }
                                    size="small"
                                    variant="filled"
                                  />
                                  <Typography
                                    variant="body2"
                                    color="error"
                                    sx={{ maxWidth: 150, fontSize: '0.75rem' }}
                                  >
                                    {result.error_message}
                                  </Typography>
                                </Box>
                              )}

                              {hasErrorMessage && !isBlocking && (
                                <Typography variant="body2" color="error" sx={{ maxWidth: 200 }}>
                                  {result.error_message}
                                </Typography>
                              )}

                              {hasSkippedItems && (
                                <Tooltip
                                  title={
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}
                                      >
                                        Parsing Failures:
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{ display: 'block', mb: 1, opacity: 0.9 }}
                                      >
                                        These HTML elements matched the item selector but were
                                        missing required fields. They may be ads, placeholders, or
                                        elements needing selector refinement.
                                      </Typography>
                                      {skippedItems.slice(0, 10).map((item: any, idx: number) => (
                                        <Typography
                                          key={idx}
                                          variant="caption"
                                          sx={{ display: 'block', mb: 0.5 }}
                                        >
                                          Item #{item.item_index}: Missing{' '}
                                          {Array.isArray(item.missing_fields)
                                            ? item.missing_fields.join(', ')
                                            : item.missing_fields}
                                        </Typography>
                                      ))}
                                      {skippedItems.length > 10 && (
                                        <Typography
                                          variant="caption"
                                          sx={{ display: 'block', mt: 1 }}
                                        >
                                          ... and {skippedItems.length - 10} more
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  arrow
                                >
                                  <Chip
                                    label={`${skippedItems.length} items skipped`}
                                    color="warning"
                                    size="small"
                                    variant="outlined"
                                    sx={{ cursor: 'help' }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          );
                        })()}
                      </TableCell>
                    </TableRow>

                    {/* Collapsible Products Row */}
                    <TableRow>
                      <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                        <Collapse in={expandedRows.has(result.id)} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: isDarkMode ? 'grey.800' : 'grey.50' }}>
                            {(() => {
                              // Dedicated seller view
                              if (isSellerResult(result)) {
                                return (
                                  <SellerCard
                                    data={result.extracted_data}
                                    taskResults={taskResults}
                                    onOpenImage={openLightbox}
                                  />
                                );
                              }

                              // Get the products to display
                              let products: ProductListing[] = [];

                              if (result.listings && Array.isArray(result.listings)) {
                                products = result.listings;
                              } else if (Array.isArray(result.extracted_data)) {
                                products = result.extracted_data;
                              } else if (
                                result.extracted_data?.items &&
                                Array.isArray(result.extracted_data.items)
                              ) {
                                products = result.extracted_data.items;
                              }

                              if (products.length === 0) {
                                return (
                                  <Typography variant="body2" color="text.secondary">
                                    No products found to display
                                  </Typography>
                                );
                              }

                              // Search URL rows: show search-result cards only; full products have their own URL rows.
                              const stage1Products = products.filter(isSearchStageListing);
                              const stage2Products = products.filter(isProductStageListing);

                              let filteredProducts: ProductListing[];
                              if (stage1Products.length > 0) {
                                filteredProducts = stage1Products;
                              } else if (stage2Products.length > 0) {
                                filteredProducts = stage2Products;
                              } else {
                                filteredProducts = products;
                              }

                              filteredProducts = filteredProducts.sort((a: any, b: any) => {
                                const aProd = isProductStageListing(a) ? 1 : 0;
                                const bProd = isProductStageListing(b) ? 1 : 0;
                                return aProd - bProd; // search results first
                              });

                              // Optionally hide duplicates
                              let duplicatesHiddenCount = 0;
                              if (hideDuplicates) {
                                const beforeLen = filteredProducts.length;
                                filteredProducts = filteredProducts.filter(p => !p.is_duplicate);
                                duplicatesHiddenCount = beforeLen - filteredProducts.length;
                              }

                              if (filteredProducts.length === 0) {
                                return (
                                  <Typography variant="body2" color="text.secondary">
                                    {duplicatesHiddenCount > 0
                                      ? `All ${duplicatesHiddenCount} products here are hidden as duplicates. Toggle 'Hide duplicates' off above to view them.`
                                      : 'Full product details are shown in the product URL rows below'}
                                  </Typography>
                                );
                              }

                              return (
                                <Grid container spacing={2}>
                                  {filteredProducts.map((product, index) => (
                                    <Grid
                                      item
                                      xs={12}
                                      md={6}
                                      lg={4}
                                      key={`${result.id}-product-${index}`}
                                    >
                                      <ProductCard
                                        listing={product}
                                        index={index}
                                        resultId={result.id}
                                        onOpenImage={openLightbox}
                                      />
                                    </Grid>
                                  ))}
                                </Grid>
                              );
                            })()}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Global Lightbox */}
      <LightboxDialog open={lightboxOpen} src={lightboxSrc} onClose={closeLightbox} />

      <VerifyAccessDialog
        open={captchaDialogOpen}
        task={captchaTask}
        onClose={() => setCaptchaDialogOpen(false)}
        onStatusMessage={message => {
          setCaptchaStatusMessage(message);
        }}
      />

      <Snackbar
        open={!!captchaStatusMessage}
        autoHideDuration={4000}
        onClose={() => setCaptchaStatusMessage(null)}
        message={captchaStatusMessage ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </Box>
  );
};

export default TaskResults;
